/**
 * The two plate designs, side by side, for the same tool.
 *
 * Choosing between them from a written description does not work - the
 * difference that matters is how six rows sit on a plate a millimetre shorter
 * than five rows needed. So both are rendered for the tool actually in front of
 * the person choosing, with their own values on them.
 *
 * Nothing is saved by looking. The mould keeps whatever design it had until
 * somebody presses the button, because a preview that quietly edits the record
 * is not a preview.
 */
import { useEffect, useState } from 'react';
import { Check, Loader2 } from 'lucide-react';
import { Dialog } from '../../components/ui/dialog.js';
import { Button } from '../../components/ui/button.js';
import { isNetworkError, listNameplateDesigns, nameplatePreview } from '../../api/client.js';
import { cn } from '../../lib/cn.js';

export const DESIGN_A_CODE = 'NP-JAY-90x55';
export const DESIGN_B_CODE = 'NP-JAY-90x54-OWNER';

export const DESIGN_LABEL: Record<string, string> = {
  [DESIGN_A_CODE]: 'Design A',
  [DESIGN_B_CODE]: 'Design B',
};

interface DesignInfo {
  code: string;
  /** The row a mould points at when it uses this design. */
  templateId: string | null;
  name: string;
  summary: string;
  plate: { w: number; h: number };
  rows: string[];
  holes: number;
}

export function NameplateCompareDialog({
  assetCode,
  current,
  onChoose,
  onClose,
}: {
  assetCode: string;
  current: string;
  onChoose: (design: DesignInfo) => Promise<void> | void;
  onClose: () => void;
}) {
  const [designs, setDesigns] = useState<DesignInfo[] | null>(null);
  const [previews, setPreviews] = useState<Record<string, string>>({});
  const [picked, setPicked] = useState(current);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const list = await listNameplateDesigns();
        if (cancelled) return;
        setDesigns(list.data);

        // Rendered one at a time rather than in parallel: each plate builds a
        // QR and the server is doing this for a person watching a dialog, not
        // for a batch.
        for (const d of list.data) {
          try {
            const svg = await nameplatePreview(assetCode, d.code);
            if (cancelled) return;
            setPreviews((p) => ({ ...p, [d.code]: svg }));
          } catch (e) {
            if (cancelled) return;
            // A design this tool cannot carry yet is worth saying so about,
            // in its own panel, rather than failing the whole comparison.
            setPreviews((p) => ({
              ...p,
              [d.code]: `error:${e instanceof Error ? e.message : 'Could not render this design.'}`,
            }));
          }
        }
      } catch (e) {
        if (!cancelled) {
          setError(
            isNetworkError(e)
              ? 'No connection. The plate is drawn on the server.'
              : e instanceof Error
                ? e.message
                : 'Could not load the designs.',
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [assetCode]);

  async function use() {
    setSaving(true);
    try {
      const chosen = designs?.find((d) => d.code === picked);
      if (chosen) await onChoose(chosen);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save the choice.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      size="lg"
      title="Which nameplate design?"
      description={`Both drawn for ${assetCode}, with its own values. Nothing is saved until you choose.`}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => void use()} loading={saving} disabled={picked === current}>
            <Check className="size-4" />
            {picked === current ? 'Already using this' : `Use ${DESIGN_LABEL[picked] ?? 'this one'}`}
          </Button>
        </>
      }
    >
      {error ? (
        <p role="alert" className="mb-3 rounded-lg bg-danger-subtle px-3 py-2 text-sm text-danger">
          {error}
        </p>
      ) : null}

      {designs === null ? (
        <div className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" /> Drawing both plates…
        </div>
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">
          {designs.map((d) => {
            const preview = previews[d.code];
            const failed = preview?.startsWith('error:');
            const chosen = picked === d.code;
            return (
              <button
                key={d.code}
                type="button"
                onClick={() => setPicked(d.code)}
                aria-pressed={chosen}
                className={cn(
                  'rounded-xl border p-3 text-left transition-colors',
                  chosen
                    ? 'border-primary bg-primary-subtle'
                    : 'border-border hover:border-border-strong',
                )}
              >
                <div className="mb-2 flex items-center justify-between gap-2">
                  <span className="font-medium">
                    {DESIGN_LABEL[d.code] ?? d.name}
                    {d.code === current ? (
                      <span className="ml-2 text-xs font-normal text-muted-foreground">in use</span>
                    ) : null}
                  </span>
                  {chosen ? <Check className="size-4 text-primary" aria-hidden /> : null}
                </div>

                <div className="mb-2 rounded-lg border border-border bg-white p-2">
                  {!preview ? (
                    <div className="flex h-28 items-center justify-center text-xs text-muted-foreground">
                      <Loader2 className="mr-1.5 size-3.5 animate-spin" /> drawing…
                    </div>
                  ) : failed ? (
                    <p className="p-2 text-xs text-warning-foreground">{preview.slice(6)}</p>
                  ) : (
                    // The server's own SVG, at whatever width the panel gives it.
                    <div
                      className="[&>svg]:h-auto [&>svg]:w-full"
                      // The markup is this application's own render of a plate,
                      // built from the mould's fields - not anything a user typed.
                      dangerouslySetInnerHTML={{ __html: preview }}
                    />
                  )}
                </div>

                <p className="mb-1.5 text-xs text-muted-foreground">{d.summary}</p>
                <dl className="flex flex-wrap gap-x-4 gap-y-0.5 text-xs">
                  <div className="flex gap-1">
                    <dt className="text-muted-foreground">Size</dt>
                    <dd>
                      {d.plate.w} × {d.plate.h} mm
                    </dd>
                  </div>
                  <div className="flex gap-1">
                    <dt className="text-muted-foreground">Holes</dt>
                    <dd>{d.holes}</dd>
                  </div>
                  <div className="flex gap-1">
                    <dt className="text-muted-foreground">Rows</dt>
                    <dd>{d.rows.length}</dd>
                  </div>
                </dl>
              </button>
            );
          })}
        </div>
      )}
    </Dialog>
  );
}
